import * as React from "react";

import { cn } from "@/lib/utils";

// 목록형 선택 (Figma "Dropdown / 기업 소재국" 546:5377): 48px 입력 모양 버튼 + 펼침 목록(반경 12, 항목 37, 선택 항목 #FFF5ED · 500).
// 열면 테두리가 주황, 선택하면 닫혀요. 바깥 클릭 · Esc로 닫고 ↑ ↓ Enter로 고를 수 있어요.
export interface NvDropdownFieldProps {
  label: React.ReactNode;
  required?: boolean;
  error?: string;
  placeholder?: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

function NvDropdownField({ label, required, error, placeholder, options, value, onChange, disabled, className }: NvDropdownFieldProps) {
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Escape") setOpen(false);
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(Math.max(0, options.findIndex((o) => o.value === value)));
        return;
      }
      setActive((i) => (e.key === "ArrowDown" ? Math.min(options.length - 1, i + 1) : Math.max(0, i - 1)));
    } else if ((e.key === "Enter" || e.key === " ") && open && active >= 0) {
      e.preventDefault();
      choose(options[active].value);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative flex flex-col gap-[6px]", className)}>
      <span id={`${id}-label`} className="text-nv-label-md text-nv-text-strong">
        {label}
        {required && " *"}
      </span>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-labelledby={`${id}-label`}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setActive(Math.max(0, options.findIndex((o) => o.value === value)));
        }}
        onKeyDown={onKeyDown}
        className={cn(
          "flex h-12 w-full items-center justify-between rounded-nv-control border bg-nv-surface px-[14px] text-left text-nv-body-md focus-visible:outline-none",
          open ? "border-nv-brand" : error ? "border-nv-danger" : "border-nv-border-strong",
          selected ? "text-nv-text-strong" : "text-nv-text-placeholder",
          disabled && "bg-nv-bg-subtle text-nv-text-disabled",
        )}
      >
        <span>{selected?.label ?? placeholder}</span>
        <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={cn("text-nv-text-placeholder transition-transform", open && "rotate-180")}>
          <path d="M6 9.5l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`} className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-nv-card border border-nv-border bg-nv-surface shadow-nv-popup">
          {options.map((o, i) => (
            <li key={o.value} role="option" aria-selected={o.value === value}>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => choose(o.value)}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "block h-[37px] w-full px-[14px] text-left text-[14px] leading-[17px] text-nv-text-primary",
                  o.value === value ? "bg-nv-brand-subtle font-medium" : i === active ? "bg-nv-bg-subtle" : "hover:bg-nv-bg-subtle",
                )}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-nv-caption text-nv-danger">{error}</p>}
    </div>
  );
}

export { NvDropdownField };
